import { User } from "../models/User";
import { UserSettings } from "../models/UserSettings";
import { CreateInternalUserRequest } from "../types/auth";
import JSONPatchOperation from "../utils/JSONPatchOperation";
import apiClient from "./api-client";
import { handleApiError } from "./error-handler";

/**
 * Get a user by ID
 * @param userId User ID
 */
export const getUser = async (userId: number): Promise<User> => {
	try {
		const response = await apiClient.get(`/users/${userId}`);
		return response.data;
	} catch (error: any) {
		throw handleApiError(error, "Failed to fetch user");
	}
};

/**
 * Create a new user
 */
export const createUser = async (user: User): Promise<User> => {
	try {
		const response = await apiClient.post(`/users`, user);
		return response.data;
	} catch (error: any) {
		throw handleApiError(error, "User creation failed");
	}
};

/**
 * Create a new internal user auth record (CSM/TAM/Admin).
 * Accepts both the identity fields and the optional Account Management profile
 * fields (phone + scheduling URLs) so an admin can populate them at creation.
 */
export const createUserAuth = async (userData: CreateInternalUserRequest): Promise<User> => {
	try {
		const response = await apiClient.post(`/auth/internal-users`, userData);
		return response.data;
	} catch (error: any) {
		throw handleApiError(error, "User authentication creation failed");
	}
};

/**
 * Get users by role
 */
export const getUsersByRole = async (role: string): Promise<User[]> => {
	try {
		const response = await apiClient.get(`/users?role=${role}`);
		// Handle both array and paginated response
		return Array.isArray(response.data) ? response.data : response.data.items ?? [];
	} catch (error: any) {
		console.error("Failed to fetch users by role:", error);
		return [];
	}
};

/**
 * Get users by company
 */
export const getUsersByCompany = async (companyId: number): Promise<User[]> => {
	try {
		const response = await apiClient.get(`/users?companyId=${companyId}`);
		// Handle both array and paginated response
		return Array.isArray(response.data) ? response.data : response.data.items ?? [];
	} catch (error: any) {
		console.error("Failed to fetch users by company:", error);
		return [];
	}
};

/**
 * Search users by criteria
 */
export const searchUsers = async (criteria: {
	email?: string;
	firstName?: string;
}): Promise<User[]> => {
	try {
		const response = await apiClient.post(`/users/search`, criteria);
		return response.data;
	} catch (error: any) {
		console.error("Error occurred while searching users:", error);
		throw handleApiError(error, "Failed to search users");
	}
};

/**
 * Get current user
 */
export const getCurrentUser = async (): Promise<User | null> => {
	try {
		const response = await apiClient.get(`/users/me`);
		return response.data;
	} catch (error: any) {
		if (error.response?.status === 404) {
			return null;
		}
		throw new Error(error.response?.data?.message ?? "Not able to get the current user.");
	}
};

/**
 * Get user settings by user ID
 */
export const getUserSettings = async (userId: number): Promise<UserSettings> => {
	try {
		const response = await apiClient.get(`/users/${userId}/settings`);
		return response.data;
	} catch (error: any) {
		throw handleApiError(error, "Failed to fetch user settings");
	}
};

/**
 * Update user settings using JSON Patch operations
 */
export const patchUserSettings = async (
	userId: number,
	operations: JSONPatchOperation[]
): Promise<void> => {
	try {
		await apiClient.patch(`/users/${userId}/settings`, operations);
	} catch (error: any) {
		throw handleApiError(error, "Failed to update user settings");
	}
};
